<template>
    <div class="large-container">
        <div class="w-full rounded-sm bg-view-sea-blue py-16">
            <div
                v-if="variant === 'contact'"
                class="container flex flex-col items-center gap-16 px-8 md:flex-row"
            >
                <div
                    v-if="profilePicture"
                    class="aspect-square w-full max-w-52 shrink-0 self-center rounded-full bg-cover bg-center md:max-w-30 lg:w-42"
                    :style="{ backgroundImage: `url('/api/files/image/${profilePicture}')` }"
                ></div>

                <div class="flex flex-col items-center gap-8 text-center md:items-start md:text-left">
                    <div class="view-h3">{{ getLocalizedValue(leftSide?.title) }}</div>
                    <div>{{ getLocalizedValue(leftSide?.body) }}</div>
                    <div v-if="leftSide?.links?.length" class="flex flex-col gap-0 underline">
                        <a
                            v-for="link in leftSide.links"
                            :key="`${link.url}-${link.label}`"
                            :href="link.url"
                        >
                            {{ link.label }}
                        </a>
                    </div>
                </div>

                <div class="flex flex-col items-center gap-8 text-center md:items-start md:text-left">
                    <div class="view-h3">{{ getLocalizedValue(rightSide?.title) }}</div>
                    <div>{{ getLocalizedValue(rightSide?.body) }}</div>
                    <div v-if="rightSide?.links?.length" class="flex flex-col gap-0 underline">
                        <a
                            v-for="link in rightSide.links"
                            :key="`${link.url}-${link.label}`"
                            :href="link.url"
                        >
                            {{ link.label }}
                        </a>
                    </div>
                </div>
            </div>

            <div
                v-else-if="variant === 'more-info'"
                class="container flex flex-col items-center justify-center gap-16 md:grid md:grid-cols-12"
            >
                <div class="view-h1 col-span-full text-center md:col-start-2 md:col-end-5 md:text-left">
                    {{ textUtils.get_hyphenated_content(resolvedTitle) }}
                </div>

                <div
                    v-if="profilePicture"
                    class="col-span-full aspect-square h-auto w-52 max-w-full shrink-0 self-center justify-self-center rounded-full bg-cover bg-center md:col-start-6 md:col-end-8 md:w-30 lg:w-42"
                    :style="{ backgroundImage: `url('/api/files/image/${profilePicture}')` }"
                ></div>

                <div class="col-span-full flex flex-col items-center gap-16 md:col-start-8 md:col-end-12 md:gap-8">
                    <div class="max-w-2xl text-center md:text-left">
                        <RenderedHTML :value="text" />
                    </div>
                    <NuxtLink
                        :to="resolvedButton.url"
                        class="self-center rounded-full border px-4 py-2 md:self-start"
                    >
                        {{ resolvedButton.label }}
                    </NuxtLink>
                </div>
            </div>

            <div
                v-else
                class="container flex flex-col items-center justify-center gap-16 md:grid md:grid-cols-12"
            >
                <div class="view-h1 col-span-full text-center md:col-start-2 md:col-end-6 md:text-left">
                    {{ textUtils.get_hyphenated_content(resolvedTitle) }}
                </div>

                <div class="col-span-full flex flex-col items-center gap-8 md:col-start-7 md:col-end-12 md:items-start">
                    <div class="max-w-2xl text-center md:text-left">
                        <RenderedHTML :value="text" />
                    </div>
                    <NuxtLink
                        :to="resolvedButton.url"
                        class="rounded-full border px-4 py-2"
                    >
                        {{ resolvedButton.label }}
                    </NuxtLink>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { JSONContent } from "@tiptap/vue-3"
import RenderedHTML from "~/components/view/RenderedHTML.vue"
import type { LocalizedText } from "#layers/cms/shared/types/bucketData"

type ContactBannerVariant = "contact" | "more-info" | "simple"

type ContactBannerSide = {
    title: LocalizedText
    body: LocalizedText
    links?: Array<{
        label: string
        url: string
    }>
}

type ContactBannerButton = {
    label?: string
    url?: string
}

const props = withDefaults(defineProps<{
    variant?: ContactBannerVariant
    title?: string | LocalizedText
    text?: LocalizedText
    profilePicture?: string
    leftSide?: ContactBannerSide
    rightSide?: ContactBannerSide
    button?: ContactBannerButton
}>(), {
    variant: "more-info",
})

const localePath = useLocalePath()
const { t } = useI18n({ useScope: "local" })
const textUtils = useTextUtils()
const { get_value: getLocalizedValue } = useLocalizedValue()

const resolvedTitle = computed(() => getLocalizedValue(props.title))

const resolvedButton = computed(() => ({
    label: props.button?.label ?? t("contact-us"),
    url: localePath(props.button?.url ?? "/contact"),
}))

const {
    variant,
    profilePicture,
    leftSide,
    rightSide,
    text,
} = toRefs(props)
</script>

<i18n lang="yaml">
en:
    contact-us: Contact us
nl:
    contact-us: Neem contact met ons op
</i18n>
